import Constants from '../../utils/Constants';
import useInProgress from './hooks/useInProgress';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useCallback } from 'react';
import InProgressFilter from './components/InProgressFilter';
import Variables from '../../style/variables.scss';
import InProgressApartmentModal from './components/InProgressApartmentModal';
import InProgressContent from './components/InProgressContent';
import InProgressBuilding from './components/InProgressBuilding';
import InProgressBuildingModal from './components/InProgressBuildingModal';
import initializeInProgressList from './data/InProgressList';
import OpeningAnimation from '../../components/opening_animation/OpeningAnimation';

const InProgress = () => {
  const {
    openAnimation,
    setOpenAnimation,
    setApartments,
    isInitialized,
    setIsInitialized,
    filters,
    setFilters,
    openApartmentModal,
    openBuildingModal,
    openFilter,
    setOpenFilter,
    activeApartment,
    setActiveApartment,
    filterApartments,
    clearAllFilters,
    handleBuildingFilter,
    handleFloorFilter,
    setOpenApartmentModal,
    setOpenBuildingModal,
  } = useInProgress();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const filteredApartments = filterApartments;

  let header: HTMLElement | null = document.getElementById('nav_container');

  // Function to load the apartments data when the component mounts
  useEffect(() => {
    const loadApartments = async () => {
      if (isInitialized) return; // Prevent re-initialization

      try {
        const loadedApartments = await initializeInProgressList();
        setApartments(loadedApartments);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to load apartments:', error);
      }
    };

    loadApartments();
  }, [isInitialized]);

  const openActiveBuildingModal = useCallback(
    (building: string, floor: string) => {
      clearAllFilters();
      setOpenBuildingModal(true);
      handleBuildingFilter(building);
      handleFloorFilter(floor);
    },
    [
      clearAllFilters,
      handleBuildingFilter,
      handleFloorFilter,
      setOpenBuildingModal,
    ]
  );

  const openActiveApartmentModal = useCallback(
    (apartmentId: string) => {
      const foundApartment = filteredApartments.find(
        (apartment) =>
          `${apartment.building}${apartment.apartmentNumber}` === apartmentId
      );

      if (foundApartment) {
        setActiveApartment(foundApartment);
        setOpenApartmentModal(true);
        navigate(`/${Constants.INPROGRESS_PAGE}/${apartmentId}`, {
          replace: false,
        });
      }
    },
    [filteredApartments, navigate, setActiveApartment, setOpenApartmentModal]
  );

  const closeApartmentModal = useCallback(() => {
    setOpenAnimation(true);
    setOpenApartmentModal(false);
    setActiveApartment(null);
    setOpenBuildingModal(false);
    navigate(`/${Constants.INPROGRESS_PAGE}`);
    setTimeout(() => clearAllFilters(), 0);
  }, [
    setOpenAnimation,
    clearAllFilters,
    navigate,
    setActiveApartment,
    setOpenApartmentModal,
    setOpenBuildingModal,
  ]);

  const backFromApartmentModal = useCallback(() => {
    setOpenApartmentModal(false);
    setActiveApartment(null);
    navigate(`/${Constants.INPROGRESS_PAGE}`);
  }, [navigate, setActiveApartment, setOpenApartmentModal]);

  const closeBuildingModal = useCallback(() => {
    setOpenAnimation(true);
    setOpenBuildingModal(false);
    setTimeout(() => clearAllFilters(), 0);
  }, [clearAllFilters, setOpenBuildingModal, setOpenAnimation]);

  const handleOpenFilter = useCallback(() => {
    document.body.classList.add('lock_scroll');
    setOpenFilter(true);
  }, [setOpenFilter]);

  useEffect(() => {
    if (id) {
      openActiveApartmentModal(id);
    }
  }, [id, openActiveApartmentModal]);

  setTimeout(() => {
    if (header !== null) {
      header.style.top = '0';
    } 
  }, 1200);

  return (
    <div id={Constants.INPROGRESS_PAGE} className='inprogress_page'>
      <OpeningAnimation isOpen={!isInitialized} isInitial />
      <OpeningAnimation isOpen={openAnimation} isInitial={false} />
      <div className='inprogress_page_box' onClick={handleOpenFilter}>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 512 512'
          height='36'
          width='36'
          fill={Variables.primaryColor}
        >
          <path d='M3.9 54.9C10.5 40.9 24.5 32 40 32l432 0c15.5 0 29.5 8.9 36.1 22.9s4.6 30.5-5.2 42.5L320 320.9 320 448c0 12.1-6.8 23.2-17.7 28.6s-23.8 4.3-33.5-3l-64-48c-8.1-6-12.8-15.5-12.8-25.6l0-79.1L9 97.3C-.7 85.4-2.8 68.8 3.9 54.9z' />
        </svg>
      </div>
      <InProgressBuilding openActiveBuildingModal={openActiveBuildingModal} />
      <div className='inprogress_page_form'>
        <InProgressFilter
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          filters={filters}
          setFilters={setFilters}
        />
        <InProgressContent
          openActiveApartmentModal={openActiveApartmentModal}
          filteredApartments={filteredApartments}
        />
      </div>
      <InProgressApartmentModal
        backFromModal={backFromApartmentModal}
        activeApartment={activeApartment}
        openModal={openApartmentModal}
        closeModal={closeApartmentModal}
      />
      <InProgressBuildingModal
        selectedFloor={filters.floor}
        handleFloorFilter={handleFloorFilter}
        openActiveApartmentModal={openActiveApartmentModal}
        filteredApartments={filteredApartments}
        openModal={openBuildingModal}
        closeModal={closeBuildingModal}
      />
    </div>
  );
};

export default InProgress;
